<template>
  <div class="initiatives-view mh-0 mw-0">
    <div class="filter-section d-flex mt-1 pr-0 pr-lg-4">
      <div class="col-12 px-0">
        <initiatives-filter
          :filter-data="filterData"
          class="mb-3"
          :org-id="orgId"
          :sub-org-id="subOrgId"
          :team-id="teamId"
          :project-id="projectId"
          :default-filters="defaultFilters"
          :default-filters-list="defaultFiltersList"
          :show-save-as-list="showSaveAsList"
          :hide-save-as-list="hideSaveAsList"
          @change="onFilterChange"
          @reset="resetFilters"
          @remove-filter="removeFilter"
          @download="downloadInitiativesWrapper"
        >
          <template slot="start">
            <div class="view-actions d-flex align-items-center">
              <view-switcher class="mr-2" :selected="selectedView" @change="onViewChange" />
              <group-by-dropdown
                :group-by="groupBy"
                @group="onGrouping"
                :org-id="orgId"
                :include-sections-grouping="!!projectId"
              />
            </div>
          </template>
        </initiatives-filter>
      </div>
    </div>
    <info-box class="mx-auto mt-4" v-if="!isFetching && groups.length === 0">
      <template slot="message">
        <div>
          There is currently no <b>items</b> to see here.
          <br />
          Try changing your filters
        </div>
      </template>
    </info-box>
    <board-view
      ref="initiativeView"
      :goals="goals"
      :groups="groups"
      :is-fetching-groups="isFetching"
      :view-id="viewId"
      :group-info="isFetching ? [] : groupInfo"
      :group-by="groupBy"
      @add="initiateAddInitiative"
      @update="onInitiativeUpdate"
      @save="onInitiativeSave"
      @remove="onInitiativeRemove"
      v-else-if="selectedView === BOARD_VIEW"
    />
    <initiatives-list-view
      ref="initiativeView"
      :groups="groups"
      :is-fetching-groups="isFetching"
      :view-id="viewId"
      :group-info="groupInfo"
      :group-by="groupBy"
      :team-id="teamId"
      :org-id="orgId"
      :next-url="apiResponse.next"
      :sort-meta="sortMeta"
      :fields-to-display="fieldsToDisplayInTable"
      :is-initial-view-loaded="isLoaded"
      :hide-gripper="hideGripperInListView"
      :show-parent="showParent"
      :project-id="projectId"
      :auto-save-column-settings="autoSaveColumnSettings"
      @add="initiateAddInitiative"
      @update="onInitiativeUpdate"
      @save="onInitiativeSave"
      @remove="onInitiativeRemove"
      @fetch-next="fetchNextListOfInitiatives"
      @sort="onSort"
      @reset-sort="onResetSort"
      @add-column="onColumnAddition"
      @section-added="addSection"
      v-else-if="selectedView === LIST_VIEW"
    />
    <max-initiatives-paywall-modal
      v-model="isPaywallModalShown"
      :org-id="orgId"
      v-if="isPaywallModalShown"
    />
    <bulk-action-tray />
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import BoardView from '@/components/board/BoardView'
import GroupByDropdown from '@/components/board/GroupByDropdown'
import InitiativesListView from '@/components/board/InitiativesListView'
import ViewSwitcher from '@/components/board/ViewSwitcher'
import BulkActionTray from '@/components/bulk-actions/BulkActionTray.vue'
import InfoBox from '@/components/InfoBox.vue'
import InitiativesFilter from '@/components/initiative/InitiativesFilter.vue'
import MaxInitiativesPaywallModal from '@/components/paywalls/MaxInitiativesPaywallModal.vue'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { NO_COLOR } from '@/constants/common'
import { CUSTOM_FIELD_TYPE, CUSTOM_FIELD_TYPE_KEY } from '@/constants/customFields'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import { featureFlags } from '@/constants/featureFlags'
import {
  BOARD_VIEW,
  CF_PREFIX,
  DEFAULT_GROUP_BY,
  DEFAULT_IMPACT,
  DEFAULT_STATUS,
  FILTER_OP_SEPARATOR,
  GROUP_BY_NONE_ID,
  GROUP_BY_SECTION_ID,
  INITIATIVE_FIELDS,
  INITIATIVE_STANDARD_FIELDS,
  InitiativeFieldType,
  LIST_VIEW,
  PROJECT_DEFAULT_GROUP_BY,
  SHOW_INITIATIVE_ID,
  STATUS,
  SYSTEM_FIELDS,
  VIEW_QUERY_PARAM
} from '@/constants/initiative'
import { CARDINALITY, InitiativeFieldId } from '@/constants/initiative/filter'
import { formatHyphenatedString } from '@/goalUtils'
import BulkActionsMixin from '@/mixins/BulkActionsMixin'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import InitiativeDisplayFieldsMixin from '@/mixins/InitiativeDisplayFieldsMixin'
import InitiativesMixin from '@/mixins/InitiativesMixin'
import TeamMixin from '@/mixins/TeamMixin'
import UserListsMixin from '@/mixins/UserListsMixin'
import { parseNQL } from '@/nql'
import cleanJSON from '@/utils/cleanJSON'
import deserializeFilterOp from '@/utils/initiative/deserializeFilterOp'
import getCustomFieldCellWidth from '@/utils/initiative/getCustomFieldCellWidth'
import getHexColorValueFromCode from '@/utils/initiative/getHexColorValueFromCode'
import getIdFromCFPrefixedField from '@/utils/initiative/getIdFromCFPrefixedField'
import getNewInitiativePayload from '@/utils/initiative/getNewInitiativePayload'
import getOperatorCardinality from '@/utils/initiative/getOperatorCardinality'
import isFieldACustomField from '@/utils/initiative/IsFieldACustomField'
import sanitizeFilterData from '@/utils/initiative/sanitizeFilterData'
import random from '@/utils/random'
import rejectNullEmptyKeys from '@/utils/rejectNullEmptyKeys'
import sanitizeQueryParams from '@/utils/sanitizeQueryParams'
import showAPIError from '@/utils/showAPIError'
import sortInitiativeFields from '@/utils/sortInitiativeFields'
import * as R from 'ramda'
import { mapActions, mapGetters } from 'vuex'

const STATUSES = Object.values(STATUS)

const PageType = {
  ALL_INITIATIVES: 'ALL_INITIATIVES',
  INITIATIVE_LIST: 'INITIATIVE_LIST',
  TEAM_PAGE: 'TEAM_PAGE',
  PROJECT_PAGE: 'PROJECT_PAGE'
}

/**
 * This is the component to show Initiatives of team/org
 *
 * props:
 * - teamId: required when board view is for team
 * - goals: list of all goals of selected team/org
 * - orgId
 * - query: filter params are passed to url as query params. parent component should pass back the same
 */
export default {
  name: 'InitiativesView',
  props: [
    'teamId',
    'subOrgId',
    'orgId',
    'query',
    'selectedTimeline',
    'defaultFiltersList',
    'showSaveAsList',
    'hideSaveAsList',
    'projectId',
    'showParent',
    'autoSaveColumnSettings' // This controls whether column setting should be saved to server
  ],
  mixins: [
    DragAndDropMixin,
    EventBusMixin,
    TeamMixin,
    FeaturesMixin,
    InitiativeDisplayFieldsMixin,
    InitiativesMixin,
    UserListsMixin,
    BulkActionsMixin
  ],
  data() {
    const defaultView = this.teamId && !this.projectId ? BOARD_VIEW : LIST_VIEW
    const defaultFilters = {
      resolution: 'any',
      // groupBy: DEFAULT_GROUP_BY[selectedView],
      view: defaultView,
      show: SHOW_INITIATIVE_ID.initiatives,
      ...(this.selectedTimeline ? { timeline: this.selectedTimeline.id } : {})
    }
    const processQuery = R.compose(
      rejectNullEmptyKeys,
      sanitizeQueryParams
    )
    const query = processQuery(this.$route.query)
    if (query.filterOp) {
      query.filterOp = [query.filterOp].flat().filter(Boolean)
    }
    const filterData = {
      ...defaultFilters,
      ...query
    }
    return {
      cachedKrData: {},
      boardData: null,
      isFetching: true,
      viewId: 0, // used to re-render content when API fails so that the dragged card goes back to previous position
      statusKey: INITIATIVE_FIELDS.STATUS.id,
      BOARD_VIEW,
      LIST_VIEW,
      goals: [],
      filterData,
      defaultFilters,
      isPaywallModalShown: false,
      isInitiativeAdditionAllowed: true,
      apiResponse: {},
      defaultView,
      isLoaded: false
    }
  },
  components: {
    BulkActionTray,
    InfoBox,
    BoardView,
    InitiativesListView,
    InitiativesFilter,
    GroupByDropdown,
    ViewSwitcher,
    MaxInitiativesPaywallModal
  },
  computed: {
    ...mapGetters(['customFieldDefinitionById']),
    ...mapGetters('initiative', ['filterConfiguration']),
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    groups() {
      return R.propOr([], 'groups', this.boardData)
    },
    krs() {
      return R.flatten(this.goals.map(goal => goal.keyResults.filter(x => !x.isArchived)))
    },
    customFieldDefinitions() {
      return this.$store.getters.customFieldDefinitions(this.orgId)
    },
    selectedKrs() {
      const filterOps = [this.filterData.filterOp].flat().filter(Boolean)
      const filterValues = filterOps
        .filter(filterOp =>
          filterOp.startsWith(`${InitiativeFieldId.KEY_RESULT}${FILTER_OP_SEPARATOR}`)
        )
        .map(filterOp => deserializeFilterOp(filterOp).value)
        .flat()
        .filter(Boolean)
      return R.uniq(filterValues).map(krId => this.$store.getters.keyResultById(+krId))
    },
    defaultGroupBy() {
      return this.projectId
        ? PROJECT_DEFAULT_GROUP_BY[this.selectedView]
        : DEFAULT_GROUP_BY[this.selectedView]
    },
    groupBy() {
      return this.query.groupBy || this.defaultGroupBy
    },
    selectedView() {
      return this.query.view || this.defaultView
    },
    groupInfo() {
      return this.groups.map((_, index) => ({
        name: this.getGroupName(index),
        icon: this.getGroupIcon(index),
        formattedGroupName: this.getFormattedGroupName(index),
        color: this.getGroupColor(index),
        isSection: this.isGroupedBySection
      }))
    },
    isGroupedByCF() {
      return this.groupBy?.startsWith(CF_PREFIX)
    },
    isGroupedBySection() {
      return this.groupBy === GROUP_BY_SECTION_ID
    },
    isFlatList() {
      return this.groupBy === GROUP_BY_NONE_ID
    },
    sortMeta() {
      // sorting is not available in grouped view
      return this.isFlatList
        ? {
            sortBy: this.query.sortBy,
            sortDirection: this.query.sortDirection
          }
        : {}
    },
    filterDataDisplayFields() {
      const { customFieldDefinitionIds, standardFieldNames } = this.filterData
      return {
        customFieldDefinitionIds: (customFieldDefinitionIds || '')
          .split(',')
          .map(Number)
          .filter(cfId => cfId && !isNaN(cfId)),
        standardFieldNames: (standardFieldNames || '').split(',').filter(Boolean)
      }
    },
    fieldsToDisplayInTable() {
      if (this.currentPage === PageType.PROJECT_PAGE) {
        return this.fieldsToDisplay
      }
      const { customFieldDefinitionIds, standardFieldNames } = this.filterDataDisplayFields
      let fields = INITIATIVE_STANDARD_FIELDS
      if (customFieldDefinitionIds.length > 0 || standardFieldNames.length > 0) {
        const customFields = R.uniq(customFieldDefinitionIds)
          .map(cfId => this.customFieldDefinitionById(cfId))
          .filter(Boolean)
          .map(fieldDetails => ({
            ...fieldDetails,
            width: getCustomFieldCellWidth(fieldDetails),
            title: fieldDetails.name,
            fieldType: InitiativeFieldType.CUSTOM_FIELD
          }))
        const systemFields = sortInitiativeFields(
          R.uniq(standardFieldNames)
            .map(sfId => SYSTEM_FIELDS.find(field => field.name === sfId))
            .filter(Boolean)
        )
        fields = [...systemFields, ...customFields]
      }
      return fields
    },
    currentPage() {
      if (this.selectedListName) return PageType.INITIATIVE_LIST
      else if (this.projectId) return PageType.PROJECT_PAGE
      else if (this.teamId) return PageType.TEAM_PAGE
      else return PageType.ALL_INITIATIVES
    },
    preventRearrangeInSameGroup() {
      return (
        this.currentPage === PageType.ALL_INITIATIVES ||
        this.filterData.show !== SHOW_INITIATIVE_ID.initiatives
      )
    },
    hideGripperInListView() {
      return this.preventRearrangeInSameGroup && this.isFlatList
    }
  },
  methods: {
    ...mapActions([
      'patchInitiative',
      'changeRank',
      'changeOwnership',
      'getInitiatives',
      'downloadInitiatives',
      'getOrgCustomFields'
    ]),
    getCFDefinition(fieldId) {
      const cfs = this.$store.state.orgCustomFields[this.orgId]
      const cfDefinition = cfs.find(x => x.id === getIdFromCFPrefixedField(fieldId)) // fieldId looks like cf_<cf_id>
      return cfDefinition
    },
    getGroupByField(groupBy) {
      switch (groupBy) {
        case INITIATIVE_FIELDS.OWNER.id:
          return 'ownerId'
        case GROUP_BY_SECTION_ID:
          return 'sectionId'
        default:
          return groupBy
      }
    },
    groupInitiatives({ results, groupHeaders }) {
      if (!groupHeaders) {
        groupHeaders = []
      }
      let groups = Array.from(groupHeaders).fill([])
      if (groups.length === 0) {
        groups = [results]
      }
      const cfDefinition = this.isGroupedByCF ? this.getCFDefinition(this.groupBy) : null
      let initiativeParseIndex = 0
      groupHeaders.forEach((header, index) => {
        let startIndex = initiativeParseIndex
        let endIndex = initiativeParseIndex
        if (this.isGroupedByCF) {
          let hasReachedEnd = false
          while (!hasReachedEnd && endIndex < results.length) {
            const initiativeCustomFieldValue = results[endIndex].customFields.find(
              cf => cf.definitionId === cfDefinition.id
            )
            const valueKey = CUSTOM_FIELD_TYPE_KEY[cfDefinition.type]
            if (
              (header === null && !initiativeCustomFieldValue) ||
              initiativeCustomFieldValue?.[valueKey] === header
            ) {
              endIndex++
            } else {
              hasReachedEnd = true
            }
          }
        } else {
          const groupBy = this.getGroupByField(this.groupBy)
          while (endIndex < results.length && results[endIndex][groupBy] === header) {
            endIndex++
          }
        }
        groups[index] = results.slice(startIndex, endIndex)
        initiativeParseIndex = endIndex
      })
      if (R.last(groupHeaders) === null) {
        groupHeaders.pop()
        groupHeaders.unshift(null)
        const lastGroup = R.last(groups)
        groups.pop()
        groups.unshift(lastGroup)
      }
      return { groups, groupHeaders }
    },
    processFilterOp(filterOp) {
      if (!filterOp) return null
      // remove filterOp with no value
      filterOp = [filterOp].flat()
      return filterOp.filter(op => {
        const { operator, value } = deserializeFilterOp(op)
        if (!operator) return false
        const minExpectedValue = this.getMinimumExpectedValueOfOperator(operator)
        return value.filter(Boolean).length >= minExpectedValue
      })
    },
    getMinimumExpectedValueOfOperator(operator) {
      const cardinality = getOperatorCardinality(this.filterConfiguration, operator)
      if (cardinality === CARDINALITY.ZERO) {
        return 0
      } else {
        return 1
      }
    },
    async getStandardFields() {
      if (this.selectedView === LIST_VIEW) {
        await this.fetchDisplayFieldIfNotPresent()
        return this.fieldsToDisplayInTable
          .filter(x => x.fieldType === InitiativeFieldType.COMPUTED_FIELD)
          .map(x => x.name)
          .join(',')
      }
    },
    getHasParentFromShowParameter(show) {
      if (show === SHOW_INITIATIVE_ID.initiatives) {
        return false
      } else if (show === SHOW_INITIATIVE_ID.tasks) {
        return true
      }
      return null
    },
    async getFilterParams() {
      const standardFields = await this.getStandardFields()
      const filterParams = {
        intervalStart: this.selectedTimeline?.start,
        intervalEnd: this.selectedTimeline?.end,
        intervalSubset: this.selectedTimeline?.subset,
        isArchived: false,
        orgId: this.orgId,
        scopeId: this.subOrgId,
        groupBy: this.isFlatList ? null : this.groupBy,
        hasParent: this.getHasParentFromShowParameter(this.filterData.show),
        includeCustomFields: this.isGroupedByCF || this.selectedView === LIST_VIEW,
        includeStandardFields: standardFields,
        filterOp: this.processFilterOp(this.filterData.filterOp),
        search: this.filterData.search,
        ...(this.isFlatList ? { limit: 100 } : { groupBy: this.groupBy }),
        orderBy: this.getOrderByFromSortParams(this.filterData)
      }
      const selectedResolution = this.filterData.resolution
      if (selectedResolution) {
        if (selectedResolution === 'closed') {
          filterParams.isClosed = true
        } else if (selectedResolution === 'open') {
          filterParams.isClosed = false
        }
      }
      if (this.projectId) {
        filterParams.projectId = this.projectId
      } else {
        filterParams.teamIds = this.filterData.teamIds || this.teamId
      }
      if (this.filterData.nql) {
        filterParams.nql = JSON.stringify(parseNQL(this.filterData.nql).toJSON())
      }
      return filterParams
    },
    addSection(section) {
      this.boardData = {
        groups: [...this.boardData.groups, []],
        groupHeaders: [...this.boardData.groupHeaders, section.id]
      }
      this.$emit('section-added')
      this.$nextTick(() => {
        setTimeout(this.makeInitiativesDraggable, 1000)
      })
    },
    /**
     *
     * @param preventLoading
     * @param appendToList is required for pagination case
     * @returns {Promise<void>}
     */
    async refreshData(preventLoading, appendToList) {
      !preventLoading && (this.isFetching = true)
      try {
        let filterParams = await this.getFilterParams()
        if (appendToList) {
          filterParams = {
            ...filterParams,
            url: this.apiResponse.next
          }
        }
        const response = await this.getInitiatives(filterParams)
        this.apiResponse = {
          ...response,
          results: [...(appendToList ? this.apiResponse.results : []), ...response.results]
        }
        this.boardData = this.groupInitiatives(this.apiResponse)
        this.$nextTick(() => {
          setTimeout(this.makeInitiativesDraggable, 1000)
        })
        R.flatten(this.boardData.groups).forEach(initiative =>
          this.$store.commit('SET_INITIATIVE', initiative)
        )
        this.isLoaded = true
      } catch (e) {
        showAPIError(e)
      }
      this.isFetching = false
    },
    fetchNextListOfInitiatives() {
      this.refreshData(true, true)
    },
    showPaywall() {
      this.isPaywallModalShown = true
    },
    initiateAddInitiative(groupIndex) {
      if (this.projectId) {
        const initiative = {
          id: this.createInitiativeId(),
          ...getNewInitiativePayload(null, '', this.orgId, this.projectId),
          additionalField: {
            fieldName: this.groupBy,
            fieldValue: this.boardData.groupHeaders[groupIndex]
          }
        }
        if (this.isGroupedBySection) {
          initiative.sectionId = this.boardData.groupHeaders[groupIndex]
        } else if (!isFieldACustomField(this.groupBy)) {
          initiative[this.groupBy] = this.boardData.groupHeaders[groupIndex]
        }
        this.boardData.groups[groupIndex].push(initiative)
        this.boardData = {
          ...this.boardData
        }
      } else {
        this.eventBusEmit(events.ADD_INITIATIVE, {
          fieldName: this.groupBy,
          fieldValue: this.getGroupName(groupIndex),
          onCreate: this.refreshData.bind(this, true),
          krsQuery: {
            interval_start: this.selectedTimeline?.start,
            interval_end: this.selectedTimeline?.end,
            interval_subset: this.selectedTimeline?.subset,
            team_id: this.teamId
          },
          projectId: this.projectId
        })
      }
    },
    addInitiative(groupIndex) {
      if (!this.isInitiativeAdditionAllowed) {
        this.showPaywall()
        return
      }
      let krData = {}
      if (this.selectedKrs.length === 1) {
        krData.keyResultId = this.selectedKrs[0]['id']
        krData.goalId = this.selectedKrs[0]['goalId']
      }
      const newInitiative = {
        id: 'local-' + random(),
        organizationId: this.orgId,
        ...krData,
        ...this.cachedKrData,
        impact: DEFAULT_IMPACT,
        status: DEFAULT_STATUS
      }
      if (this.isGroupedByCF) {
        newInitiative[this.groupBy] = this.boardData.groupHeaders[groupIndex]
      } else {
        newInitiative[this.groupBy] = this.getGroupName(groupIndex)
      }
      const group = this.boardData.groups[groupIndex] || []
      group.push(newInitiative)
      this.boardData = { ...this.boardData }
    },
    getGroupName(index) {
      return R.pathOr('', ['groupHeaders', index], this.boardData)
    },
    getFormattedGroupName(index) {
      const groupName = this.getGroupName(index)
      if (this.groupBy === INITIATIVE_FIELDS.OWNER.id) {
        const ownerId = this.boardData.groupHeaders[index]
        if (ownerId) {
          const user = this.$store.getters.userById(ownerId)
          return user ? user.displayName : null
        } else {
          return 'Unassigned'
        }
      } else if (this.groupBy === INITIATIVE_FIELDS.STATUS.id) {
        const status = STATUSES.find(R.propEq('id', groupName))
        return status ? status.label : formatHyphenatedString(groupName)
      } else if (this.isGroupedByCF) {
        const fieldId = getIdFromCFPrefixedField(this.groupBy)
        const allCfDefs = this.customFieldDefinitions
        const cfdef = allCfDefs.find(def => def.id === fieldId)
        if (cfdef.type === 'dropdown') {
          const dopId = this.boardData.groupHeaders[index]
          if (dopId) {
            return cfdef.dropdownOptions.find(dop => dop.id === dopId).name
          } else {
            return 'Not Assigned'
          }
        } else if (cfdef.type === 'user') {
          const userId = this.boardData.groupHeaders[index]
          if (userId) {
            const user = this.$store.getters.userById(userId)
            return user ? user.displayName : null
          } else {
            return 'Unassigned'
          }
        }
      } else if (this.isGroupedBySection) {
        const section = this.$store.getters['project/sectionById'](groupName)
        return section?.title
      }
      return formatHyphenatedString(groupName)
    },
    updateBoardDataAfterDrop({
      initiative,
      beforeGroupIndex,
      afterGroupIndex,
      afterId,
      subjectId
    }) {
      const beforeGroupData = this.boardData.groups[beforeGroupIndex]

      if (!initiative) {
        // initiative dropped to same group
        initiative = beforeGroupData.find(x => x.id === +subjectId)
      }
      // remove initiative from source group
      this.boardData.groups[beforeGroupIndex] = beforeGroupData.filter(x => x.id !== +subjectId)

      // add initiative to target group
      let index = -1
      if (afterId) {
        index = this.boardData.groups[afterGroupIndex].findIndex(x => x.id === +afterId)
      }
      const updatedGroup = R.insert(index + 1, initiative, this.boardData.groups[afterGroupIndex])
      this.boardData.groups = R.update(+afterGroupIndex, updatedGroup, this.boardData.groups)
      this.boardData = { ...this.boardData }
    },
    async updateInitiative(subjectId, afterGroupIndex, beforeGroupIndex) {
      if (this.groupBy === INITIATIVE_FIELDS.OWNER.id) {
        const ownerId = this.getGroupName(afterGroupIndex)
        const entity = this.boardData.groups[beforeGroupIndex].find(R.propEq('id', +subjectId))
        return await this.changeOwnership({
          orgId: this.orgId,
          entityType: ENTITY_TYPE.INITIATIVE,
          entity,
          ownerId,
          coOwnerIds: entity.coOwnerIds.filter(userId => userId !== ownerId)
        })
      } else if (this.isGroupedByCF) {
        const fieldId = getIdFromCFPrefixedField(this.groupBy)
        const fields = this.customFieldDefinitions
        const field = fields.find(field => field.id === fieldId)
        const nextValue = this.getGroupName(afterGroupIndex)
        return await this.$store.dispatch('setInitiativeCustomField', {
          orgId: this.orgId,
          initiativeId: subjectId,
          field: field,
          value: nextValue
        })
      } else {
        const groupByField = this.getGroupByField(this.groupBy)
        return await this.patchInitiative({
          orgId: this.orgId,
          initiativeId: subjectId,
          data: {
            [groupByField]: this.getGroupName(afterGroupIndex)
          }
        })
      }
    },
    onDropError() {
      this.viewId++
      this.$nextTick(function() {
        // This should be inside nextTick because drag and drop should be re-enabled after list is rendered
        setTimeout(this.makeInitiativesDraggable, 1000)
      })
    },
    async onDrop({ afterId, beforeId, subjectId, container, targetContainer }) {
      const afterGroupIndex = container.dataset.groupIndex
      const beforeGroupIndex = targetContainer.dataset.groupIndex
      const isTasksAndInitiativesShown = this.filterData.show === SHOW_INITIATIVE_ID.any
      if (!isTasksAndInitiativesShown && (beforeId || afterId)) {
        // initiatives cannot be dropped between tasks
        // update initiative rank
        try {
          await this.changeRank({
            orgId: this.orgId,
            type: 'initiative',
            subjectId,
            beforeId,
            afterId
          })
        } catch (e) {
          this.onDropError()
          return
        }
      }
      let initiative
      if (afterGroupIndex !== beforeGroupIndex) {
        // update initiative
        try {
          initiative = await this.updateInitiative(subjectId, afterGroupIndex, beforeGroupIndex)
        } catch (e) {
          showAPIError(e)
          this.onDropError()
          return
        }
      }
      this.updateBoardDataAfterDrop({
        initiative,
        beforeGroupIndex,
        afterGroupIndex,
        afterId,
        subjectId
      })
    },
    makeInitiativesDraggable() {
      this.enableDragAndDrop({
        containers: this.groups.map((group, index) => {
          let ref = this.$refs.initiativeView?.$refs['group-' + this.getGroupName(index)]
          ref = ref && ref[0]
          return this.selectedView === LIST_VIEW ? ref?.$refs?.initiatives : ref
        }),
        onDrop: this.onDrop,
        handleName: 'drag-handle',
        isFullyDraggable: this.selectedView === BOARD_VIEW,
        preventDropInSameContainer: this.preventRearrangeInSameGroup,
        preventDropAtEnd: this.selectedView === LIST_VIEW, // because last item is placeholder to add initiative
        scrollingContainers: [[document.querySelector('.initiatives-view-scroll-container')]]
      })
    },
    onInitiativeRemove({ initiative, groupIndex }) {
      let group = this.boardData.groups[groupIndex] || []
      group = group.filter(x => x.id !== initiative.id)
      const groups = R.update(groupIndex, group, this.boardData.groups)
      this.boardData = { ...this.boardData, groups }
    },
    onInitiativeSave({ after, before, groupIndex }) {
      const group = this.boardData.groups[groupIndex] || []
      const initiativeIndex = group.findIndex(x => x.id === before.id)
      const groupInitiatives = R.update(initiativeIndex, after, this.boardData.groups[groupIndex])
      const groups = R.update(groupIndex, groupInitiatives, this.boardData.groups)
      this.boardData = { ...this.boardData, groups }
      this.cachedKrData = { goalId: after.goalId, keyResultId: after.keyResultId }
      if (group.length > 1) {
        // change rank so that initiative come at the end of the column
        const afterId = R.nth(-2, group)['id']
        this.changeRank({
          orgId: this.orgId,
          type: 'initiative',
          subjectId: after.id,
          afterId
        })
      }
      this.setIsInitiativeAdditionAllowed()
    },
    onSort({ by, direction }) {
      this.refreshQuery({ sortBy: by, sortDirection: direction })
    },
    onResetSort() {
      this.refreshQuery({ sortBy: '', sortDirection: '' })
    },
    onColumnAddition(params) {
      this.refreshQuery(params)
    },
    onInitiativeUpdate({ initiative, groupIndex }) {
      const group = this.boardData.groups[groupIndex] || []
      const initiativeIndex = group.findIndex(x => x.id === initiative.id)
      const groupInitiatives = R.update(
        initiativeIndex,
        initiative,
        this.boardData.groups[groupIndex]
      )
      const groups = R.update(groupIndex, groupInitiatives, this.boardData.groups)
      this.boardData = { ...this.boardData, groups }
    },
    refreshQuery(newQuery, replaceHistory, replaceQuery) {
      let query = replaceQuery ? newQuery : { ...this.query, ...newQuery }
      query = cleanJSON(query)
      const action = replaceHistory ? this.$router.replace : this.$router.push
      action.call(this.$router, {
        name: this.$route.name,
        params: this.$route.params,
        query
      })
    },
    trackResetFilter() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.RESET_FILTER, EVENT_NAME.BOARD_VIEW)
    },
    trackGrouping() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.APPLY_GROUPING, EVENT_NAME.BOARD_VIEW)
    },
    onGrouping(groupBy) {
      this.refreshQuery({ groupBy })
      this.trackGrouping()
      this.isLoaded = false
    },
    onViewChange(selectedView) {
      const params = { [VIEW_QUERY_PARAM]: selectedView }
      if (selectedView === BOARD_VIEW) {
        trackEvent(
          EVENT_CATEGORY.NAVIGATION,
          EVENT_ACTION.CHANGE_INITIATIVE_VIEW,
          EVENT_NAME.BOARD_VIEW
        )
        if (this.isFlatList) {
          params.groupBy = ''
        }
      } else {
        trackEvent(
          EVENT_CATEGORY.NAVIGATION,
          EVENT_ACTION.CHANGE_INITIATIVE_VIEW,
          EVENT_NAME.LIST_VIEW
        )
      }
      this.refreshQuery(params)
      this.isLoaded = false
    },
    getHexColorValueFromCode,
    getGroupColor(index) {
      if (this.isGroupedByCF) {
        const fieldId = getIdFromCFPrefixedField(this.groupBy)
        const cfDefinition = this.customFieldDefinitions.find(def => def.id === fieldId)
        if (cfDefinition.type === CUSTOM_FIELD_TYPE.DROPDOWN) {
          const color = cfDefinition.dropdownOptions[index - 1]?.color // doing "index - 1" because "unassigned" column is at the index = 0, because of which actual indices of all the options shifted by 1
          return color && color !== NO_COLOR && getHexColorValueFromCode(color) + 'd8' // adding d8 adds opacity to bg color to ~85%
        }
      } else if (this.groupBy === INITIATIVE_FIELDS.STATUS.id) {
        const status = STATUSES.find(R.propEq('id', this.boardData.groupHeaders[index]))
        return status?.color
      }
    },
    getGroupIcon(index) {
      if (this.groupBy === INITIATIVE_FIELDS.STATUS.id) {
        const status = STATUSES.find(R.propEq('id', this.boardData.groupHeaders[index]))
        return status?.icon
      }
    },
    async fetchGoals() {
      this.goals = await this.$store.dispatch('getGoals', {
        orgId: this.orgId,
        teamId: this.teamId,
        options: {
          intervalStart: this.selectedTimeline.start,
          intervalEnd: this.selectedTimeline.end,
          intervalSubset: this.selectedTimeline.subset,
          isArchived: this.team ? this.team.isArchived : false
        }
      })
    },
    refreshDataOnUpdate() {
      const updateEvents = [
        events.INITIATIVE_UPDATED,
        events.INITIATIVES_DELETED,
        events.INITIATIVES_MOVED
      ]
      updateEvents.forEach(event =>
        this.listenToEventBus(event, () => {
          this.refreshData(true)
          event === events.INITIATIVES_DELETED &&
            !this.isInitiativeAdditionAllowed &&
            this.setIsInitiativeAdditionAllowed()
        })
      )
    },
    trackViewLaunchEvent() {
      if (this.selectedView === BOARD_VIEW && this.teamId) {
        let action = EVENT_ACTION.OPEN_TEAM_BOARD_VIEW
        if (this.team.isOrgLike) {
          action = EVENT_ACTION.OPEN_ORG_BOARD_VIEW
        }
        trackEvent(EVENT_CATEGORY.NAVIGATION, action)
      }
    },
    setFilterData(filterData) {
      this.filterData = filterData
    },
    resetFilters() {
      if (this.currentPage === PageType.INITIATIVE_LIST) {
        this.setFilterData({
          ...this.selectedList.filterData,
          listId: this.selectedList.id
        })
      } else {
        const filterOp =
          this.filterData.filterOp && [this.filterData.filterOp].flat().map(R.always(''))
        this.setFilterData({
          ...this.defaultFilters,
          ...(filterOp && { filterOp })
        })
      }
      const { sortBy, sortDirection } = this.$route.query
      this.refreshQuery({
        ...this.filterData,
        sortBy,
        sortDirection
      })
    },

    async onFilterChange({ field, fieldValue }) {
      let query = this.$route.query
      query = {
        ...query,
        [field]: fieldValue
      }
      this.refreshQuery(query)
    },
    removeFilter({ index, field }) {
      const query = this.$route.query
      let newQuery = query
      if (['search', 'nql'].includes(field.id)) {
        newQuery = R.omit([field.id], query)
      } else {
        if (query.filterOp) {
          newQuery = {
            ...query,
            filterOp: R.remove(index, 1, [query.filterOp].flat()) // if there is only one field, filterOp will be a string
          }
        }
      }
      this.refreshQuery(newQuery, null, true)
    },
    updateRouteWithFilterData() {
      this.$router.replace({
        ...this.$route,
        query: {
          view: BOARD_VIEW,
          // groupBy: DEFAULT_BOARD_GROUP_BY,
          ...this.filterData
        }
      })
    },
    async downloadInitiativesWrapper({ format, compareWithDate = null }) {
      const filterParams = await this.getFilterParams()
      const { customFieldDefinitionIds, standardFieldNames } = this.filterDataDisplayFields
      const payload = {
        ...filterParams,
        includeCustomFields: customFieldDefinitionIds ? customFieldDefinitionIds.join(',') : '',
        includeStandardFields: standardFieldNames ? standardFieldNames.join(',') : '',
        format: format,
        compareWithDate: compareWithDate
      }
      this.downloadInitiatives(rejectNullEmptyKeys(payload))
    },
    async setIsInitiativeAdditionAllowed() {
      this.isInitiativeAdditionAllowed = await this.satisfyFeature(
        this.orgId,
        featureFlags.MAX_INITIATIVE_COUNT
      )
    }
  },
  async mounted() {
    this.filterConfiguration && this.refreshData()
    !this.projectId && this.fetchGoals()
    this.refreshDataOnUpdate()
    this.trackViewLaunchEvent()
    this.updateRouteWithFilterData()
    this.setIsInitiativeAdditionAllowed()
    this.listenToEventBus(events.BULK_ACTION_DONE, () => {
      this.refreshData(true)
    })
  },
  watch: {
    selectedView: 'trackViewLaunchEvent',
    query(query, oldQuery) {
      const filterData = this.filterData
      const defaultParams = {
        ...this.defaultFilters,
        view: this.defaultView
        // groupBy: DEFAULT_GROUP_BY[BOARD_VIEW]
      }
      this.setFilterData({ ...this.defaultFilters, ...rejectNullEmptyKeys(query) })
      const currentFilterParams = sanitizeFilterData({
        ...defaultParams,
        ...rejectNullEmptyKeys(query)
      })
      const previousFilterParams = sanitizeFilterData({
        ...defaultParams,
        ...rejectNullEmptyKeys(oldQuery)
      })

      if (!R.equals(currentFilterParams, previousFilterParams) && this.filterConfiguration) {
        this.refreshData().catch(() => {
          // reset changes
          this.setFilterData(filterData)
        })
      }
    },
    filterConfiguration(current, previous) {
      !previous && current && this.refreshData()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.initiatives-view {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.filter-section {
  display: flex;
  justify-content: center;
  width: 100%;
  white-space: nowrap;
}

.view-actions {
  margin-top: 5px;
  margin-right: 8px;
  border-radius: 30px;
  background-color: $quaternary;
  padding: 4px 6px;
}

/deep/ .initiatives-head {
  .row-wrapper {
    background: $documentColor;
  }
}

@include media-breakpoint-down(md) {
  .filter-section {
    padding-left: 0;
  }
}
</style>
